import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Clock, CheckCircle2, XCircle, Calendar, Lightbulb, Link2, User } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import type { Suggestion, StatusEnum } from "@/types/api";
import { suggestionsApi } from "@/api/suggestions";

interface SuggestionCardProps {
  suggestion: Suggestion;
  showCounts?: boolean;
  showAvatar?: boolean;
}

const SuggestionCard = ({ suggestion, showCounts = false, showAvatar = false }: SuggestionCardProps) => {
  const navigate = useNavigate();
  const [recommendationsCount, setRecommendationsCount] = useState<number>(0);
  const [followupsCount, setFollowupsCount] = useState<number>(0);
  const [countsLoading, setCountsLoading] = useState(false);

  useEffect(() => {
    if (!showCounts) return;

    const fetchCounts = async () => {
      setCountsLoading(true);
      try {
        const [recommendations, followups] = await Promise.all([
          suggestionsApi.getRecommendations(suggestion.id),
          suggestionsApi.getFollowups(suggestion.id)
        ]);
        setRecommendationsCount(recommendations.length);
        setFollowupsCount(followups.length);
      } catch (error) {
        console.error(`Error fetching counts for suggestion ${suggestion.id}:`, error);
      } finally {
        setCountsLoading(false);
      }
    };

    fetchCounts();
  }, [suggestion.id, showCounts]);

  const hasRecommendations = recommendationsCount > 0;
  const hasFollowups = followupsCount > 0;
  const showBadges = showCounts && (hasRecommendations || hasFollowups);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status: StatusEnum) => {
    switch (status) {
      case "pending":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-700">
          <Clock className="w-3 h-3" /> Pending
        </span>;
      case "completed":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Completed
        </span>;
      case "dismissed":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-700">
          <XCircle className="w-3 h-3" /> Dismissed
        </span>;
      case "scheduled":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
          <Calendar className="w-3 h-3" /> Scheduled
        </span>;
      case "expired":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-50 text-red-700">
          <XCircle className="w-3 h-3" /> Expired
        </span>;
      default:
        return null;
    }
  };

  return (
    <div
      onClick={() => navigate(`/suggestions/${suggestion.id}`)}
      className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          {showAvatar && (
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-10 h-10">
                {suggestion.contact_avatar ? (
                  <AvatarImage
                    src={`/images/avatars/${suggestion.contact_avatar}`}
                    alt={suggestion.contact_name}
                  />
                ) : null}
                <AvatarFallback className="bg-gray-200">
                  <User className="w-5 h-5 text-gray-500" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-gray-900">{suggestion.contact_name}</span>
            </div>
          )}

          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-gray-900 group-hover:text-wabisabi-plum transition-colors">
              {suggestion.title}
            </h3>
            {getStatusBadge(suggestion.status)}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
            {suggestion.content}
          </p>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="capitalize">{suggestion.suggestion_type}</span>
            <span>•</span>
            <span>{formatDate(suggestion.created_at)}</span>
          </div>
          {showBadges && (
            <div className="flex items-center gap-3 text-sm text-gray-700 mt-2">
              {hasRecommendations && (
                <div className="flex items-center gap-1">
                  <Lightbulb className="w-4 h-4 text-blue-600" />
                  <span>{recommendationsCount} recommendation{recommendationsCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              {hasFollowups && (
                <div className="flex items-center gap-1">
                  <Link2 className="w-4 h-4 text-purple-600" />
                  <span>{followupsCount} nudge{followupsCount !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuggestionCard;
